import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

// Static data untuk Self-Service
const selfServiceData = [
  {
    id: 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      employeeId: 'EMP001'
    },
    serviceType: 'Pengajuan Cuti',
    requestDate: '2024-01-10',
    startDate: '2024-01-15',
    endDate: '2024-01-17',
    status: 'Approved',
    approver: 'Budi Manager',
    description: 'Cuti tahunan untuk liburan keluarga',
    category: 'Leave'
  },
  {
    id: 2,
    employee: {
      name: 'Siti Nurhaliza',
      photo: '',
      position: 'HR Manager',
      employeeId: 'EMP002'
    },
    serviceType: 'Perubahan Data Pribadi',
    requestDate: '2024-01-12',
    startDate: '-',
    endDate: '-',
    status: 'Pending',
    approver: '-',
    description: 'Update alamat dan nomor telepon',
    category: 'Personal Info'
  },
  {
    id: 3,
    employee: {
      name: 'Budi Santoso',
      photo: '',
      position: 'Marketing Executive',
      employeeId: 'EMP003'
    },
    serviceType: 'Pengajuan Izin',
    requestDate: '2024-01-14',
    startDate: '2024-01-20',
    endDate: '2024-01-20',
    status: 'Approved',
    approver: 'Marketing Manager',
    description: 'Izin untuk keperluan keluarga',
    category: 'Leave'
  },
  {
    id: 4,
    employee: {
      name: 'Dewi Lestari',
      photo: '',
      position: 'Accountant',
      employeeId: 'EMP004'
    },
    serviceType: 'Request Dokumen',
    requestDate: '2024-01-11',
    startDate: '-',
    endDate: '-',
    status: 'Completed',
    approver: 'Finance Manager',
    description: 'Request surat keterangan kerja',
    category: 'Document'
  },
  {
    id: 5,
    employee: {
      name: 'Eko Prasetyo',
      photo: '',
      position: 'Sales Manager',
      employeeId: 'EMP005'
    },
    serviceType: 'Pengajuan Cuti',
    requestDate: '2024-01-13',
    startDate: '2024-01-25',
    endDate: '2024-01-27',
    status: 'Pending',
    approver: '-',
    description: 'Cuti untuk acara keluarga',
    category: 'Leave'
  },
  {
    id: 6,
    employee: {
      name: 'Fitri Handayani',
      photo: '',
      position: 'Designer',
      employeeId: 'EMP006'
    },
    serviceType: 'Perubahan Data Pribadi',
    requestDate: '2024-01-15',
    startDate: '-',
    endDate: '-',
    status: 'Approved',
    approver: 'Creative Director',
    description: 'Update email dan nomor telepon',
    category: 'Personal Info'
  },
  {
    id: 7,
    employee: {
      name: 'Gunawan Wijaya',
      photo: '',
      position: 'Operations Manager',
      employeeId: 'EMP007'
    },
    serviceType: 'Request Dokumen',
    requestDate: '2024-01-09',
    startDate: '-',
    endDate: '-',
    status: 'Completed',
    approver: 'Operations Director',
    description: 'Request slip gaji bulanan',
    category: 'Document'
  },
  {
    id: 8,
    employee: {
      name: 'Hani Kartika',
      photo: '',
      position: 'Customer Service',
      employeeId: 'EMP008'
    },
    serviceType: 'Pengajuan Izin',
    requestDate: '2024-01-16',
    startDate: '2024-01-22',
    endDate: '2024-01-22',
    status: 'Rejected',
    approver: 'CS Manager',
    description: 'Izin untuk keperluan pribadi',
    category: 'Leave'
  }
];

const SelfServicePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setData(selfServiceData);
      setLoading(false);
    }, 500);
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Leave':
        return <DescriptionIcon />;
      case 'Personal Info':
        return <PersonIcon />;
      case 'Document':
        return <DescriptionIcon />;
      default:
        return <CategoryIcon />;
    }
  };

  const filteredData = data
    .filter(record => {
      const matchesSearch = record.employee.name.toLowerCase().includes(searchText.toLowerCase()) ||
                           record.serviceType.toLowerCase().includes(searchText.toLowerCase()) ||
                           record.description.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === '' || record.status === statusFilter;
      const matchesCategory = categoryFilter === '' || record.category === categoryFilter;
      
      return matchesSearch && matchesStatus && matchesCategory;
    })
    .sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));

  const uniqueStatuses = [...new Set(data.map(record => record.status))];
  const uniqueCategories = [...new Set(data.map(record => record.category))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <SettingsIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Self-Service Portal
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola layanan self-service karyawan dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<RefreshIcon />} 
                onClick={() => window.location.reload()}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />} 
                onClick={() => navigate('/self-service/add')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                ✨ Buat Request
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Cari Request"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Status</em>
                    </MenuItem>
                    {uniqueStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Kategori</em>
                    </MenuItem>
                    {uniqueCategories.map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Request
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Memuat data self-service...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          No
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minWidth: 200
                        }}>
                          Karyawan
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Tipe Layanan
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Kategori
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Tanggal Request
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Periode
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Approver
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }}>
                          Aksi
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((record, index) => (
                        <TableRow 
                          key={record.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                              transform: 'scale(1.001)',
                              transition: 'all 0.2s ease'
                            },
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar 
                                sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                              >
                                {record.employee.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {record.employee.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {record.employee.position}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {record.employee.employeeId}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {record.serviceType}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              {record.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={record.category}
                              size="small"
                              icon={getCategoryIcon(record.category)}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {new Date(record.requestDate).toLocaleDateString('id-ID', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {record.startDate !== '-' ? (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(record.startDate).toLocaleDateString('id-ID', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })} - {new Date(record.endDate).toLocaleDateString('id-ID', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={record.status}
                              color={getStatusColor(record.status)}
                              size="small"
                              icon={record.status === 'Approved' || record.status === 'Completed' ? <CheckCircleIcon /> : 
                                    record.status === 'Pending' ? <PendingIcon /> : <CancelIcon />}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {record.approver !== '-' ? record.approver : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/self-service/view/${record.id}`)}
                                sx={{ color: 'primary.main' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/self-service/edit/${record.id}`)}
                                sx={{ color: 'warning.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {}}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default SelfServicePage;
